// Text entries of the context menu: cut / copy / paste / select all in fields, copy for selected text anywhere.
// Edits go through execCommand so React's onChange, the undo stack and input masks (beforeinput) all see them like
// typing; clipboard, labels and toast come from the program's configuration (config.ts).
import { ctxConfig, ctxToast } from './config';
import { shortcut, type CtxEntry } from './menu';
import type { CtxEvent } from './registry';

type Field = HTMLInputElement | HTMLTextAreaElement;
const TEXT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number', '']);

/** The editable element under the click: a text-like input, a textarea or contenteditable. */
export function editableOf(target: Element): Field | HTMLElement | null {
  const f = target.closest('input,textarea,[contenteditable=""],[contenteditable="true"]');
  if (!f) return null;
  if (f instanceof HTMLInputElement && !TEXT_TYPES.has(f.type)) return null;
  return f as Field | HTMLElement;
}
const isField = (el: Element): el is Field => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

export const writeClipboard = (text: string): Promise<boolean> => ctxConfig().clipboard.write(text);
export const readClipboard = (): Promise<string | null> => ctxConfig().clipboard.read();

/** Copies and says so (short values are shown in the toast). */
export async function copyAndTell(text: string, what: string): Promise<void> {
  const ok = await writeClipboard(text);
  const L = ctxConfig().labels;
  if (!ok) { ctxToast(L.copyFailed, 'warning'); return; }
  const shown = text.length > 40 ? text.slice(0, 38) + '…' : text;
  ctxToast(L.copied(what, shown));
}

/** Writes text into the field at the current selection the way typing does. */
function insert(el: Field | HTMLElement, text: string) {
  el.focus();
  if (document.execCommand('insertText', false, text)) return;
  if (isField(el)) {
    const s = el.selectionStart ?? el.value.length, e = el.selectionEnd ?? s;
    el.setRangeText(text, s, e, 'end');
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

/** Entries for fields and selected text; null when neither applies. */
export function textEntries(ev: CtxEvent): CtxEntry[] | null {
  const ed = editableOf(ev.target), L = ctxConfig().labels;
  if (!ed) {
    if (!ev.selection.trim()) return null;
    const sel = ev.selection;
    return [{ label: L.copy, icon: 'copy', hint: shortcut('C'), run: () => writeClipboard(sel) }];
  }
  const field = isField(ed) ? ed : null;
  // Remember the selection: the click on the menu must not lose it.
  const start = field?.selectionStart ?? null, end = field?.selectionEnd ?? null;
  const selected = field ? (start != null && end != null ? field.value.slice(start, end) : '') : ev.selection;
  const ro = field ? field.readOnly || field.disabled : false;
  const secret = field instanceof HTMLInputElement && field.type === 'password';
  const restore = () => {
    ed.focus();
    if (field && start != null && end != null) { try { field.setSelectionRange(start, end); } catch { /* number inputs */ } }
  };
  return [
    { label: L.cut, icon: 'cut', hint: shortcut('X'), disabled: !selected || ro || secret,
      run: async () => { restore(); if (await writeClipboard(selected)) { restore(); if (!document.execCommand('delete')) insert(ed, ''); } } },
    { label: L.copy, icon: 'copy', hint: shortcut('C'), disabled: !selected || secret, run: () => writeClipboard(selected) },
    { label: L.paste, icon: 'paste', hint: shortcut('V'), disabled: ro,
      run: async () => {
        const t = await readClipboard();
        if (t == null) { ctxToast(L.pasteWith(shortcut('V')), 'warning'); restore(); return; }
        restore(); insert(ed, t);
      } },
    'sep',
    { label: L.selectAll, icon: 'selectAll', hint: shortcut('A'),
      run: () => { ed.focus(); if (field) field.select(); else document.execCommand('selectAll'); } },
  ];
}
